import axios from "axios";
import { API_URL } from "../constants";

export type TrainingTestStatus = "draft" | "active" | "archived";
export type TrainingAssignmentStatus =
  | "pending"
  | "in_progress"
  | "passed"
  | "failed";

export interface TrainingQuestionOption {
  id: string;
  text: string;
}

export interface TrainingQuestion {
  id?: string;
  questionText: string;
  options: TrainingQuestionOption[];
  /** One or more correct option ids */
  correctOptionIds: string[];
  order?: number;
  /** Present on worker take-test payload (no correct ids exposed) */
  multiSelect?: boolean;
}

export interface TrainingMaterial {
  id: string;
  test: string;
  file: any;
  createdAt?: number;
  extractedText?: string | null;
  extractedAt?: number | null;
  extractStatus?: "pending" | "ready" | "empty" | "unsupported" | "error" | null;
  extractError?: string | null;
}

export interface TrainingTest {
  id: string;
  company: string;
  /**
   * Instruction area (sphere) this test belongs to — raw id, unpopulated
   * (PR-2 §P1). Present on the fetch-tests list response; used to render the
   * "Sritis" column in "all areas" mode.
   */
  area?: string | null;
  name: string;
  description?: string | null;
  passPercent: number;
  durationMinutes: number;
  maxAttempts?: number | null;
  status: TrainingTestStatus;
  questionsCount?: number;
  materialsCount?: number;
  rolesCount?: number;
  assignmentCount?: number;
  passedCount?: number;
  materials?: TrainingMaterial[];
  questions?: TrainingQuestion[];
  roles?: { id: string; name: string }[];
  roleLinks?: { id: string; role: string }[];
  createdAt?: number;
  updatedAt?: number;
}

export interface TrainingAssignment {
  id: string;
  status: TrainingAssignmentStatus;
  source: "role" | "manual";
  user?: any;
  attemptsCount?: number;
  lastAttempt?: TrainingAttemptSummary | null;
  canRemove?: boolean;
  trainingCompletedAt?: number | null;
  trainingCompleted?: boolean;
  materialsCount?: number;
  test?: {
    id: string;
    name: string;
    description?: string | null;
    durationMinutes: number;
    passPercent: number;
    maxAttempts?: number | null;
    status: TrainingTestStatus;
  };
  openAttempt?: {
    id: string;
    startedAt: number;
    expiresAt: number;
    attemptNumber: number;
  } | null;
  canStart?: boolean;
}

export interface TrainingAttemptSummary {
  id: string;
  attemptNumber: number;
  status: "in_progress" | "submitted" | "timed_out";
  startedAt: number;
  expiresAt: number;
  submittedAt?: number | null;
  scorePercent?: number | null;
  passed?: boolean | null;
  review?: TrainingReviewItem[];
}

export interface TrainingReviewItem {
  questionId: string;
  questionText: string;
  options: TrainingQuestionOption[];
  correctOptionIds: string[];
  selectedOptionIds: string[];
  /** Legacy single fields (optional) */
  correctOptionId?: string | null;
  selectedOptionId?: string | null;
  isCorrect: boolean;
}

export interface TrainingListPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export async function fetchTrainingTests(
  companyId: string,
  body: {
    page?: number;
    pageSize?: number;
    textSearch?: string;
    status?: string;
    /**
     * Instruction area to scope the list to (PR-2 §P1 hard rule): pass this
     * ONLY when a concrete area is selected among several — omit the field
     * entirely otherwise. Top-level (not nested), per the backend controller
     * (api/controllers/work-security/training/fetch-tests.js reads `body.areaId`).
     */
    areaId?: string;
  } = {}
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/fetch`;
  const { data } = await axios.post(url, body);
  return data as {
    tests: TrainingTest[];
    pagination: TrainingListPagination;
  };
}

export async function createTrainingTest(
  companyId: string,
  body: {
    name: string;
    description?: string;
    passPercent?: number;
    durationMinutes?: number;
    maxAttempts?: number | null;
    /** Instruction sphere id (optional — defaults to the company's default sphere). */
    area?: string;
  }
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests`;
  const { data } = await axios.post(url, body);
  return data as { test: TrainingTest };
}

export async function fetchTrainingTest(companyId: string, testId: string) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/${testId}`;
  const { data } = await axios.get(url);
  return data as {
    test: TrainingTest;
    aiQuestionsEnabled?: boolean;
    uploadCategoryIds?: string[];
    isUploadConfigured?: boolean;
  };
}

export async function updateTrainingTest(
  companyId: string,
  testId: string,
  body: Partial<{
    name: string;
    description: string | null;
    passPercent: number;
    durationMinutes: number;
    maxAttempts: number | null;
    status: TrainingTestStatus;
  }>
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/${testId}`;
  const { data } = await axios.patch(url, body);
  return data as { test: TrainingTest };
}

export async function attachTrainingMaterial(
  companyId: string,
  testId: string,
  fileId: string
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/${testId}/materials`;
  const { data } = await axios.post(url, { fileId });
  return data as { material: TrainingMaterial };
}

export async function removeTrainingMaterial(
  companyId: string,
  testId: string,
  materialId: string
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/${testId}/materials/${materialId}`;
  const { data } = await axios.delete(url);
  return data;
}

export async function saveTrainingQuestions(
  companyId: string,
  testId: string,
  questions: TrainingQuestion[]
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/${testId}/questions`;
  const { data } = await axios.put(url, { questions });
  return data as { questions: TrainingQuestion[] };
}

export async function generateTrainingQuestions(
  companyId: string,
  testId: string,
  body: {
    count?: number;
    sourceText?: string;
    materialIds?: string[];
  } = {}
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/${testId}/generate-questions`;
  const { data } = await axios.post(url, body);
  return data as {
    questions: TrainingQuestion[];
    usedMaterials?: { id: string; name: string }[];
    skippedMaterials?: { id: string; name: string; reason: string }[];
  };
}

export async function setTrainingTestRoles(
  companyId: string,
  testId: string,
  roleIds: string[]
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/${testId}/roles`;
  const { data } = await axios.put(url, { roleIds });
  return data;
}

export async function assignTrainingUsers(
  companyId: string,
  testId: string,
  userIds: string[]
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/${testId}/assign`;
  const { data } = await axios.post(url, { userIds });
  return data;
}

export async function removeTrainingAssignment(
  companyId: string,
  assignmentId: string
) {
  const url = `${API_URL}/${companyId}/work-security/training/assignments/${assignmentId}`;
  const { data } = await axios.delete(url);
  return data as { ok: boolean };
}

export async function fetchTrainingAssignments(
  companyId: string,
  testId: string,
  body: { page?: number; pageSize?: number; status?: string } = {}
) {
  const url = `${API_URL}/${companyId}/work-security/training/tests/${testId}/assignments/fetch`;
  const { data } = await axios.post(url, body);
  return data as {
    assignments: TrainingAssignment[];
    pagination: TrainingListPagination;
  };
}

export async function fetchMyTrainingAssignments(companyId: string) {
  const url = `${API_URL}/${companyId}/work-security/training/my-assignments`;
  const { data } = await axios.get(url);
  return data as { assignments: TrainingAssignment[] };
}

export async function fetchTrainingAssignmentDetail(
  companyId: string,
  assignmentId: string
) {
  const url = `${API_URL}/${companyId}/work-security/training/assignments/${assignmentId}`;
  const { data } = await axios.get(url);
  return data as {
    assignment: TrainingAssignment;
    materials: Array<{
      id: string;
      fileId: string;
      fileName: string;
      createdAt?: number;
    }>;
  };
}

export async function completeTrainingAssignment(
  companyId: string,
  assignmentId: string
) {
  const url = `${API_URL}/${companyId}/work-security/training/assignments/${assignmentId}/complete-training`;
  const { data } = await axios.post(url, {});
  return data as {
    assignment: TrainingAssignment;
    materials: Array<{
      id: string;
      fileId: string;
      fileName: string;
      createdAt?: number;
    }>;
    alreadyCompleted?: boolean;
  };
}

export async function startTrainingAttempt(
  companyId: string,
  assignmentId: string
) {
  const url = `${API_URL}/${companyId}/work-security/training/assignments/${assignmentId}/start`;
  const { data } = await axios.post(url, {});
  return data as {
    attempt: TrainingAttemptSummary;
    test: {
      id: string;
      name: string;
      durationMinutes: number;
      passPercent: number;
    };
    questions: Array<
      Omit<TrainingQuestion, "correctOptionIds"> & { multiSelect?: boolean }
    >;
    resumed: boolean;
  };
}

export async function submitTrainingAttempt(
  companyId: string,
  attemptId: string,
  answers: { questionId: string; selectedOptionIds: string[] }[]
) {
  const url = `${API_URL}/${companyId}/work-security/training/attempts/${attemptId}/submit`;
  const { data } = await axios.post(url, { answers });
  return data as {
    attempt: TrainingAttemptSummary;
    assignment: TrainingAssignment;
    scorePercent: number;
    passed: boolean;
    correct: number;
    total: number;
  };
}

export async function fetchTrainingAttempt(
  companyId: string,
  attemptId: string
) {
  const url = `${API_URL}/${companyId}/work-security/training/attempts/${attemptId}`;
  const { data } = await axios.get(url);
  return data;
}

export async function fetchAssignmentAttempts(
  companyId: string,
  assignmentId: string
) {
  const url = `${API_URL}/${companyId}/work-security/training/assignments/${assignmentId}/attempts`;
  const { data } = await axios.get(url);
  return data as {
    assignment: TrainingAssignment;
    attempts: TrainingAttemptSummary[];
  };
}

export function newBlankQuestion(): TrainingQuestion {
  const a = `opt-a-${Date.now()}`;
  const b = `opt-b-${Date.now()}`;
  return {
    questionText: "",
    options: [
      { id: a, text: "" },
      { id: b, text: "" },
    ],
    correctOptionIds: [a],
  };
}

/** Normalize API/legacy question shapes to correctOptionIds[] */
export function normalizeQuestionCorrectIds(
  q: TrainingQuestion & { correctOptionId?: string }
): string[] {
  if (Array.isArray(q.correctOptionIds) && q.correctOptionIds.length) {
    return q.correctOptionIds.map(String);
  }
  if (q.correctOptionId) return [String(q.correctOptionId)];
  return q.options?.[0]?.id ? [q.options[0].id] : [];
}
